import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
 
// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};
 
const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};
 
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};
 
// Renamed component
const Sanitation = () => {
  // Data for the initiatives section for cleaner code
  const initiatives = [
    "Awareness campaigns in slums and municipalities.",
    "Supporting the 'Swachh Bharat Mission' to create open-defecation free towns.",
    "Construction of community bathing steps to keep pond surroundings clean.",
    "Building 12 hi-tech model ladies' changing rooms in Bargarh district.",
  ];
 
  const { openDonation } = useDonation();
  const navigate = useNavigate();
 
  return (
    <>
      <Header />
 
      {/* Hero Section */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Sanitation_1521x482.jpg)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Sanitation
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash supports sanitation initiatives by promoting clean water, hygiene practices, and waste management to improve public health and living conditions.
            </motion.p>
          </div>
        </Container>
      </section>
 
      {/* Redesigned middle UI for Sanitation - content unchanged */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700&display=swap');
        :root{--san-teal:#0f7a3a;--san-teal-2:#1f8f4d;--san-accent:#eaf7ef;--san-radius:20px;--maxw:1200px}
        .san-wrap{max-width:var(--maxw);margin:0 auto;padding:0 16px}
        .san-section{padding:84px 0}
 
        /* decorative top curve */
        .san-curve{height:48px;overflow:hidden}
        .san-curve svg{width:100%;height:100%;display:block}
 
        .san-about{display:grid;grid-template-columns:1fr 460px;gap:36px;align-items:center;background:linear-gradient(180deg, rgba(15,122,58,0.06), rgba(255,255,255,0.0));padding:36px;border-radius:26px}
        .san-badge{display:inline-block;background:linear-gradient(90deg,var(--san-teal-2),var(--san-teal));color:#fff;padding:8px 14px;border-radius:999px;font-weight:700;margin-bottom:12px;font-family:Poppins, sans-serif}
        .san-title{font-family:Poppins, sans-serif;color:var(--san-teal);font-size:30px;font-weight:700;margin:6px 0}
        .san-card{background:#fff;border-radius:var(--san-radius);padding:26px 28px;box-shadow:0 18px 48px rgba(15,122,58,0.06);position:relative;border-left:6px solid rgba(27,142,70,0.14)}
        .san-accent{position:absolute;left:0;top:0;bottom:0;width:6px;background:linear-gradient(180deg,var(--san-teal-2),var(--san-teal));border-top-left-radius:var(--san-radius);border-bottom-left-radius:var(--san-radius)}
        .san-card p{margin:0;color:#234b48;line-height:1.85}
        .san-image{border-radius:20px;overflow:hidden;box-shadow:0 24px 60px rgba(15,122,58,0.06)}
        .san-image img{width:100%;display:block;height:auto}
 
        /* Initiatives grid - premium list */
        .san-initiatives{margin-top:48px}
        .san-initiatives .hdr{text-align:center;margin-bottom:22px}
        .san-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px}
        .san-item{background:linear-gradient(180deg,#fff, var(--san-accent));border-radius:18px;padding:22px 24px;box-shadow:0 12px 38px rgba(15,122,58,0.04);display:flex;align-items:center;gap:18px}
        .san-dot{width:58px;height:58px;border-radius:50%;background:linear-gradient(135deg,var(--san-teal-2),var(--san-teal));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-family:Poppins;margin-left:6px}
        .san-item p{margin:0;color:#163c38;font-weight:600}
        .san-item:hover{transform:translateY(-8px);box-shadow:0 36px 100px rgba(15,122,58,0.08);transition:all 320ms cubic-bezier(.2,.9,.2,1)}
        @media (max-width:991.98px){.san-about{grid-template-columns:1fr;}.san-grid{grid-template-columns:1fr}}
      `}</style>
 
      <section className="san-section" aria-labelledby="san-about-heading">
        <div className="san-wrap">
          <div className="san-curve" aria-hidden="true">
            <svg viewBox="0 0 1200 48" preserveAspectRatio="none"><path d="M0,28 C250,80 450,0 600,24 C750,48 950,10 1200,28 L1200 0 L0 0 Z" fill="#eefaf8" /></svg>
          </div>
 
          <div className="san-about">
            <div>
              <span className="san-badge">Community Health & Hygiene</span>
              <h2 className="san-title" id="san-about-heading">Promoting Health and Hygiene</h2>
              <div className="san-card">
                <div className="san-accent" aria-hidden="true"></div>
                <p>
                  Vikash actively organizes awareness campaigns in the slums of Bhubaneswar and various municipalities, complementing the government's "Swachh Bharat Mission." We focus on motivating communities to make their villages and towns clean, tidy, and open-defecation free. To further this goal, we have constructed essential infrastructure like bathing steps and hi-tech, low-cost ladies' changing rooms to improve hygiene and cleanliness in public spaces.
                </p>
              </div>
            </div>
 
            <div className="san-image" role="img" aria-label="Sanitation awareness campaign image">
              <img src="/assets/images/Subsections/s1.jpg" alt="Sanitation awareness campaign" />
            </div>
          </div>
 
          <div className="san-initiatives">
            <div className="hdr">
              <h2 className="fw-bold" style={{ color: "#0f7a6a" }}>Our Core Sanitation Projects</h2>
              <p className="lead">We focus on both awareness and infrastructure to create lasting change.</p>
            </div>
 
            <div className="san-grid">
              {initiatives.map((initiative, index) => (
                <div className="san-item" key={index}>
                  <div className="san-dot">{index + 1}</div>
                  <p>{initiative}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
 
      {/* Call To Action Section (Reusable) */}
      <section className="py-5 text-dark" style={{ backgroundColor: "#ffffffff" }}>
        <Container className="text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <h2 className="display-5 fw-bold mb-3">Support a Healthier Community</h2>
            <p className="lead mb-4">
              Your contribution helps us build essential sanitation facilities and promote hygiene education.
            </p>
            <Button variant="dark" size="lg" className="me-3 fw-bold" onClick={openDonation}>
              Donate
            </Button>
            <Button variant="outline-dark" size="lg" className="fw-bold" onClick={() => navigate("/interns")}>
              Join Us
            </Button>
          </motion.div>
        </Container>
      </section>
 
      <Footer />
 
      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};
 
export default Sanitation;
 